/**
 * Normalize `ochatbot-v2`'s widget-config payload into the shape the
 * legacy `IntelligentWebContext` (and downstream layouts) expect. The
 * v2 backend returns a structured envelope:
 *
 *   {
 *     agent: { id, name, type, isWebAi, ... },
 *     theme: { colors, layout, fontFamily, ... },
 *     streaming: { enabled, endpoint, fallback_endpoint },
 *     public_chat: { chatServer, frameEndpoint },
 *     meta: { sessionId, masterKey, ... },
 *     ctas, leadForms, ...
 *   }
 *
 * The legacy `conf` is a single flat object with `chatServer`,
 * `widgetButtonColor`, `displayMode`, etc. Renderers won't be touched
 * during the port, so we adapt the new payload to that flat shape.
 *
 * `normalizeConfig` also accepts the legacy `/api/widget/config/{key}`
 * payload unchanged, so old embeds (`data-master-key` only) keep working
 * even though the new endpoint omits streaming metadata.
 */

import { generateUuid } from './uuid.js'
import { pickEcommerceTriggerConfig } from './ecommerceTriggerConfig.js'
import { sanitizeWidgetBubbleIconSvg } from './sanitizeBubbleIcon.js'
import {
  applySlideOutConfigToConf,
  clampSlideOutPanelWidthPx,
  resolveSlideOutPanelMaxHeightFromSources,
  SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX,
} from './slideOutPanelLayout.js'
import { widgetChatDebug } from './widgetChatDebug.js'
import { normalizeSuggestionStripSize } from './suggestionCardLayout.js'

function asString(value, fallback = '') {
  if (value == null) return fallback
  return typeof value === 'string' ? value : String(value)
}

function normalizeSearchTexts(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map((t) => String(t ?? '').trim()).filter(Boolean)
}

/** Positive integer agent ids only (matches DB / widgetInternalAgentId). */
function parsePositiveInt(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  const i = Math.trunc(n)
  if (i !== n) return null
  return i
}

/**
 * Stream + launch whispers must use the external (public) agent id. Prefer
 * explicit public* / chatId / agentId on the payload; skip any candidate that
 * equals {@code widgetInternalAgentId} so an internal DB id is not promoted
 * as the visitor-facing id.
 *
 * @param {unknown[]} candidates
 * @param {number | null} internalHint
 * @returns {string}
 */
function resolveExternalWidgetAgentIdFromCandidates(candidates, internalHint) {
  for (const raw of candidates) {
    const s = asString(raw ?? '', '').trim()
    if (s === '') continue
    if (internalHint != null) {
      const n = parsePositiveInt(s)
      if (n === internalHint) continue
    }
    return s
  }
  return ''
}

/**
 * Mount options sometimes pass the internal DB id as {@code agentId}; do not
 * let that overwrite a resolved public id on the flattened conf.
 *
 * @param {Record<string, unknown>} overrides
 * @param {number | null} internalHint
 * @returns {Record<string, unknown>}
 */
function stripConflictingInternalAgentIdOverrides(overrides, internalHint) {
  if (!overrides || typeof overrides !== 'object') return {}
  if (internalHint == null) return { ...overrides }
  const out = { ...overrides }
  if (parsePositiveInt(out.agentId) === internalHint) {
    delete out.agentId
  }
  if (parsePositiveInt(out.chatId) === internalHint) {
    delete out.chatId
  }
  return out
}

function pickColor(theme, key, fallback) {
  return asString(theme?.colors?.[key] ?? theme?.[key] ?? fallback, fallback)
}

/**
 * Chat panel title — matches v1 {@link TitleBar} precedence: device/shared
 * header titles, then `title`, `widget_title`, then agent `name`.
 */
function pickEnvelopeWidgetTitle(theme, agent, payload) {
  const t = asString(theme?.title, '').trim()
  if (t) return t
  const a = agent && typeof agent === 'object' ? agent : {}
  const wt = asString(a.widget_title ?? a.widgetTitle, '').trim()
  if (wt) return wt
  const nm = asString(a.name, '').trim()
  if (nm) return nm
  return asString(
    payload?.widget_title ?? payload?.widgetTitle ?? payload?.title ?? payload?.name ?? 'Chat',
    'Chat',
  )
}

function pickEnvelopeWidgetSubtitle(theme, agent) {
  const t = asString(theme?.subtitle, '').trim()
  if (t) return t
  const a = agent && typeof agent === 'object' ? agent : {}
  return asString(
    a.widget_intro_message ?? a.widgetIntroMessage ?? '',
    '',
  )
}

function pickLegacyFlatWidgetTitle(c) {
  const cfg = c.configuration
  if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) {
    const fromCfg = asString(
      cfg.widget_title ?? cfg.widgetTitle ?? cfg.title,
      '',
    ).trim()
    if (fromCfg) return fromCfg
    const fromName = asString(cfg.name, '').trim()
    if (fromName) return fromName
  }
  const explicit = asString(c.widgetTitle, '').trim()
  if (explicit) return explicit
  const desktopHeader = c.desktop?.header || {}
  const mobileHeader = c.mobile?.header || {}
  const shared = c.shared || {}
  return asString(
    desktopHeader.title ||
      mobileHeader.title ||
      shared.title ||
      c.title ||
      shared.widget_title ||
      c.widget_title ||
      c.name ||
      'Chat',
    'Chat',
  )
}

function pickLegacyFlatWidgetSubtitle(c) {
  const explicit = asString(c.widgetSubtitle, '').trim()
  if (explicit) return explicit
  const d = c.desktop?.header || {}
  const m = c.mobile?.header || {}
  const s = c.shared || {}
  return asString(d.subtitle || m.subtitle || s.subtitle || c.subtitle || '', '')
}

/**
 * Maps v1 `displayMode` strings to v2 template keys when the API only
 * supplies `theme.layout.displayMode` (or a flat legacy `displayMode`)
 * without an explicit `template`.
 */
function deriveTemplateKeyFromDisplayMode(displayMode) {
  let m = asString(displayMode, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
  if (m.includes('slide') && m.includes('out')) m = 'slide-out'
  if (m === 'slideout') m = 'slide-out'
  if (m === 'floating' || m === 'floating-bubble') return 'floating-bubble'
  if (m === 'slide-out' || m === 'mobile') return 'slide-out'
  if (m === 'fullscreen') return 'fullscreen'
  if (m === 'injected' || m === 'inline') return 'injected'
  if (m === 'overlay' || m === 'expand') return 'panel'
  return null
}

/**
 * When only `displayMode` is set (BotMan preview, legacy embeds), `defaultConf`
 * leaves `template: 'panel'`. Align template with displayMode like the API envelope path.
 */
function applyTemplateDerivationFromDisplayMode(conf) {
  if (!conf || typeof conf !== 'object') return
  const derivedTemplate = deriveTemplateKeyFromDisplayMode(conf.displayMode)
  if (
    !derivedTemplate ||
    conf.template !== 'panel' ||
    !conf.displayMode ||
    conf.displayMode === 'overlay'
  ) {
    return
  }
  conf.template = derivedTemplate
  conf.ometricsAI = {
    ...(conf.ometricsAI && typeof conf.ometricsAI === 'object'
      ? conf.ometricsAI
      : {}),
    template: derivedTemplate,
  }
}

/** Builder preview iframe simulates mobile slide-out, not desktop overlay split. */
function applyPreviewMobileLayout(conf) {
  if (
    conf?.previewMode !== true &&
    conf?.previewMode !== 'true' &&
    conf?.previewMode !== 1
  ) {
    return
  }
  const deviceType = String(conf?.deviceType || '')
    .trim()
    .toLowerCase()
  const isPreviewMobile = deviceType === 'mobile' || conf?.isMobile === true
  if (!isPreviewMobile) return

  conf.displayMode = 'slide-out'
  conf.template = 'slide-out'
  conf.isMobile = true
  conf.ometricsAI = {
    ...(conf.ometricsAI && typeof conf.ometricsAI === 'object'
      ? conf.ometricsAI
      : {}),
    displayMode: 'slide-out',
    template: 'slide-out',
  }
}

/** Legacy BotMan preview passes relative streaming paths under `streaming`. */
function resolveLegacyStreamingEndpoints(conf, overrides = {}) {
  if (!conf || typeof conf !== 'object') return
  const apiBaseUrl = asString(
    overrides.apiBaseUrl || conf.widgetApiBaseUrl || conf.apiBaseUrl || '',
  ).replace(/\/$/, '')
  if (!apiBaseUrl) return

  const streaming =
    conf.streaming && typeof conf.streaming === 'object' ? conf.streaming : {}
  if (!streaming.endpoint && !streaming.fallback_endpoint) return

  conf.streamingEndpoint = resolveStreamingEndpoint({ streaming, apiBaseUrl })
  conf.fallbackEndpoint = resolveFallbackEndpoint({ streaming, apiBaseUrl })
  conf.chatServer = resolveChatServer({
    streaming,
    publicChat: {},
    apiBaseUrl,
  })
  conf.streamingEnabled = streaming.enabled !== false
  conf.apiBaseUrl = apiBaseUrl
}

/**
 * Some legacy callers pass `window.OmetricsAI_<id>` directly as
 * `conf`. Detect the shape and short-circuit so `normalizeConfig` is
 * idempotent.
 */
function looksLikeLegacyConfig(payload) {
  if (!payload || typeof payload !== 'object') return false
  // API envelope (GET /api/v1/widget/config)
  if (payload.agent && typeof payload.agent === 'object') return false
  if (payload.public_chat || payload.publicChat) return false
  const theme = payload.theme
  if (
    theme &&
    typeof theme === 'object' &&
    (theme.layout || theme.colors || theme.title)
  ) {
    return false
  }
  // BotMan / preview embeds may include root-level `streaming` alongside chatServer.
  return (
    'chatServer' in payload ||
    'displayMode' in payload ||
    'ometricsAI' in payload ||
    'chatId' in payload
  )
}

/**
 * Legacy `/api/widget/config/{key}` and BotMan merges expose
 * `widget_*_color` snake_case keys. Copy into the camelCase fields the
 * v2 renderers read when the admin saved a non-empty value.
 */
/**
 * BotMan / master-key previews pass agent styling under `configuration` using
 * `widget_*` DB column names. Legacy flat conf expects those at the root for
 * {@link applySnakeCaseWidgetColors}; lift so Settings-saved colors apply.
 */
function applyWidgetColorsFromNestedConfiguration(conf) {
  const cfg = conf?.configuration
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return
  const keys = [
    'widget_background_color',
    'widget_header_background_color',
    'widget_header_text_color',
    'widget_user_message_background_color',
    'widget_user_message_text_color',
    'widget_bot_message_background_color',
    'widget_bot_message_text_color',
    'widget_button_color',
    'widget_button_text_color',
    'widget_input_background_color',
    'widget_input_text_color',
    'widget_bubble_color',
    'widget_bubble_background_color',
  ]
  for (const snake of keys) {
    const raw = cfg[snake]
    if (raw == null) continue
    const v = typeof raw === 'string' ? raw.trim() : String(raw).trim()
    if (v === '') continue
    conf[snake] = v
  }
}

/** Lift AI Display launcher fields saved on `configuration` (preview / legacy). */
function applyLauncherFieldsFromNestedConfiguration(conf) {
  const cfg = conf?.configuration
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return
  const keys = [
    'widgetType',
    'searchBoxType',
    'searchTexts',
    'slideOutConfig',
    'buttonText',
    'buttonColor',
    'buttonTextColor',
    'buttonBorderColor',
    'buttonBorderRadius',
    'buttonIcon',
    'buttonIconPosition',
    'buttonIconAnimation',
  ]
  for (const key of keys) {
    if (conf[key] != null && String(conf[key]).trim?.() !== '') continue
    if (cfg[key] == null) continue
    conf[key] = cfg[key]
  }
}

function applySnakeCaseWidgetColors(conf) {
  if (!conf || typeof conf !== 'object') return
  const pairs = [
    ['widgetButtonColor', 'widget_button_color'],
    ['widgetButtonTextColor', 'widget_button_text_color'],
    ['widgetUserMessageBackgroundColor', 'widget_user_message_background_color'],
    ['widgetUserMessageTextColor', 'widget_user_message_text_color'],
    ['widgetBotMessageBackgroundColor', 'widget_bot_message_background_color'],
    ['widgetBotMessageTextColor', 'widget_bot_message_text_color'],
    ['widgetHeaderBackgroundColor', 'widget_header_background_color'],
    ['widgetHeaderTextColor', 'widget_header_text_color'],
    ['widgetPanelBackgroundColor', 'widget_background_color'],
    ['widgetInputBackgroundColor', 'widget_input_background_color'],
    ['widgetInputTextColor', 'widget_input_text_color'],
  ]
  for (const [camel, snake] of pairs) {
    const raw = conf[snake]
    if (raw == null) continue
    const v = typeof raw === 'string' ? raw.trim() : String(raw).trim()
    if (v !== '') conf[camel] = v
  }
}

function isPlainOrderStatusObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v)
}

/**
 * Master-key unified JSON puts `orderStatus` on each `instances[]` surface
 * (see ochatbot-v2 `UnifiedMasterKeyWidgetEnvelopeMapper`); the envelope root
 * often omits it. Match `mergeEnvelopeWithInstance`: prefer floating, then
 * any instance with a plain object.
 *
 * @param {Record<string, unknown>} payload
 * @returns {Record<string, unknown> | undefined}
 */
function pickOrderStatusFromUnifiedEnvelope(payload) {
  if (!payload || typeof payload !== 'object') return undefined
  if (isPlainOrderStatusObject(payload.orderStatus)) {
    return /** @type {Record<string, unknown>} */ (payload.orderStatus)
  }
  const instances = Array.isArray(payload.instances) ? payload.instances : []
  const floating = instances.find(
    (s) =>
      s &&
      typeof s === 'object' &&
      s.kind === 'floating' &&
      isPlainOrderStatusObject(s.orderStatus),
  )
  if (floating && isPlainOrderStatusObject(floating.orderStatus)) {
    return /** @type {Record<string, unknown>} */ (floating.orderStatus)
  }
  const any = instances.find(
    (s) =>
      s && typeof s === 'object' && isPlainOrderStatusObject(s.orderStatus),
  )
  return any && isPlainOrderStatusObject(any.orderStatus)
    ? /** @type {Record<string, unknown>} */ (any.orderStatus)
    : undefined
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {Array<Record<string, unknown>>}
 */
function pickMenuItemsFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload.menuItems)) return payload.menuItems
  const instances = Array.isArray(payload.instances) ? payload.instances : []
  const floating = instances.find(
    (s) => s && typeof s === 'object' && Array.isArray(s.menuItems),
  )
  if (floating && Array.isArray(floating.menuItems)) return floating.menuItems
  const any = instances.find(
    (s) => s && typeof s === 'object' && Array.isArray(s.menuItems),
  )
  return any && Array.isArray(any.menuItems) ? any.menuItems : []
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {Record<string, unknown>}
 */
function pickMenuEcommerceFromPayload(payload) {
  const fallback = { available: false }
  if (!payload || typeof payload !== 'object') return fallback
  if (
    payload.menuEcommerce &&
    typeof payload.menuEcommerce === 'object' &&
    !Array.isArray(payload.menuEcommerce)
  ) {
    return /** @type {Record<string, unknown>} */ (payload.menuEcommerce)
  }
  const instances = Array.isArray(payload.instances) ? payload.instances : []
  const floating = instances.find(
    (s) =>
      s &&
      typeof s === 'object' &&
      s.menuEcommerce &&
      typeof s.menuEcommerce === 'object',
  )
  if (floating?.menuEcommerce && typeof floating.menuEcommerce === 'object') {
    return /** @type {Record<string, unknown>} */ (floating.menuEcommerce)
  }
  const any = instances.find(
    (s) =>
      s &&
      typeof s === 'object' &&
      s.menuEcommerce &&
      typeof s.menuEcommerce === 'object',
  )
  return any?.menuEcommerce && typeof any.menuEcommerce === 'object'
    ? /** @type {Record<string, unknown>} */ (any.menuEcommerce)
    : fallback
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {number | undefined}
 */
function pickDefaultMenuConversationIdFromUnifiedEnvelope(payload) {
  if (!payload || typeof payload !== 'object') return undefined
  const root = payload.defaultMenuConversationId
  if (root != null && Number.isFinite(Number(root))) {
    return Number(root)
  }
  const instances = Array.isArray(payload.instances) ? payload.instances : []
  const floating = instances.find(
    (s) =>
      s &&
      typeof s === 'object' &&
      s.kind === 'floating' &&
      s.defaultMenuConversationId != null &&
      Number.isFinite(Number(s.defaultMenuConversationId)),
  )
  if (floating) return Number(floating.defaultMenuConversationId)
  const any = instances.find(
    (s) =>
      s &&
      typeof s === 'object' &&
      s.defaultMenuConversationId != null &&
      Number.isFinite(Number(s.defaultMenuConversationId)),
  )
  return any ? Number(any.defaultMenuConversationId) : undefined
}

/**
 * Opt-in: same toggles as `widgetChatDebug` (`?chatDebug=1`, dev, etc.).
 *
 * @param {Record<string, unknown>} conf
 * @param {unknown} sourcePayload
 * @param {string} context
 */
function traceOrderStatusResolved(conf, sourcePayload, context) {
  const os = conf?.orderStatus
  const src =
    sourcePayload && typeof sourcePayload === 'object'
      ? sourcePayload.orderStatus
      : undefined
  const srcDesc =
    src == null
      ? 'absent'
      : Array.isArray(src)
        ? 'array'
        : typeof src === 'object'
          ? 'object'
          : typeof src

  let instancesHaveOrderStatus = false
  if (sourcePayload && typeof sourcePayload === 'object') {
    const arr = Array.isArray(sourcePayload.instances)
      ? sourcePayload.instances
      : []
    instancesHaveOrderStatus = arr.some(
      (s) =>
        s &&
        typeof s === 'object' &&
        s.orderStatus != null &&
        typeof s.orderStatus === 'object',
    )
  }

  let resolved
  if (os != null && typeof os === 'object') {
    if (Array.isArray(os)) {
      resolved = { shape: 'array', length: os.length }
    } else {
      resolved = {
        shape: 'object',
        enabled: os.enabled,
        showSuggestionStrip: os.showSuggestionStrip,
        hasLookupUrl:
          typeof os.lookupUrl === 'string' && os.lookupUrl.length > 0,
        keys: Object.keys(os).sort().join(','),
      }
    }
  } else {
    resolved = { shape: typeof os }
  }

  widgetChatDebug('conf.orderStatus', {
    context,
    agentId: conf?.agentId ?? conf?.chatId ?? null,
    payloadOrderStatus: srcDesc,
    instancesHaveOrderStatus,
    resolved,
  })
}

/**
 * AI display saves defaultCardImage on configuration root; older payloads may
 * only set shared.defaultCardImage or theme.defaultCardImage.
 */
function pickDefaultCardImage(payload, theme, agent) {
  const p = payload && typeof payload === 'object' ? payload : {}
  const t = theme && typeof theme === 'object' ? theme : {}
  const a = agent && typeof agent === 'object' ? agent : {}
  const shared =
    p.shared && typeof p.shared === 'object' && !Array.isArray(p.shared)
      ? p.shared
      : null
  const cfg =
    p.configuration && typeof p.configuration === 'object' && !Array.isArray(p.configuration)
      ? p.configuration
      : null
  const cfgShared =
    cfg?.shared && typeof cfg.shared === 'object' && !Array.isArray(cfg.shared)
      ? cfg.shared
      : null
  return asString(
    p.defaultCardImage ??
      cfg?.defaultCardImage ??
      cfgShared?.defaultCardImage ??
      shared?.defaultCardImage ??
      t.defaultCardImage ??
      a.defaultCardImage ??
      '',
    '',
  )
}

/**
 * AI display saves customCss on configuration root; the server maps it to
 * theme.customCss, but older cached payloads may only carry it at the root,
 * under `configuration`, `shared`, or on the agent record.
 */
function pickCustomCss(payload, theme, agent) {
  const p = payload && typeof payload === 'object' ? payload : {}
  const t = theme && typeof theme === 'object' ? theme : {}
  const a = agent && typeof agent === 'object' ? agent : {}
  const shared =
    p.shared && typeof p.shared === 'object' && !Array.isArray(p.shared)
      ? p.shared
      : null
  const cfg =
    p.configuration && typeof p.configuration === 'object' && !Array.isArray(p.configuration)
      ? p.configuration
      : null
  const cfgShared =
    cfg?.shared && typeof cfg.shared === 'object' && !Array.isArray(cfg.shared)
      ? cfg.shared
      : null
  return asString(
    t.customCss ||
      p.customCss ||
      cfg?.customCss ||
      cfgShared?.customCss ||
      shared?.customCss ||
      a.customCss ||
      '',
    '',
  )
}

export function normalizeConfig(payload, overrides = {}) {
  if (!payload || typeof payload !== 'object') {
    const out = {
      ...defaultConf(),
      ...overrides,
    }
    traceOrderStatusResolved(out, payload, 'normalizeConfig:invalid_payload')
    return out
  }

  if (looksLikeLegacyConfig(payload)) {
    const legacyHint =
      parsePositiveInt(payload.widgetInternalAgentId) ??
      parsePositiveInt(overrides.widgetInternalAgentId)
    const safeOverrides = stripConflictingInternalAgentIdOverrides(
      overrides,
      legacyHint,
    )
    const merged = {
      ...defaultConf(),
      ...payload,
      ...pickEcommerceTriggerConfig(
        payload,
        typeof payload.configuration === 'object' &&
          payload.configuration !== null &&
          !Array.isArray(payload.configuration)
          ? payload.configuration
          : null,
      ),
      ...safeOverrides,
    }
    merged.widgetBubbleIcon = sanitizeWidgetBubbleIconSvg(
      merged.widgetBubbleIcon || merged.widget_bubble_icon,
    )
    merged.widgetTitle = pickLegacyFlatWidgetTitle(merged)
    merged.widgetSubtitle = pickLegacyFlatWidgetSubtitle(merged)
    applyWidgetColorsFromNestedConfiguration(merged)
    applyLauncherFieldsFromNestedConfiguration(merged)
    applySnakeCaseWidgetColors(merged)
    const ph = asString(merged.widget_placeholder_text || '', '').trim()
    if (ph) merged.composerPlaceholder = ph
    applySlideOutConfigToConf(merged, { authoritative: true })

    if (!merged.kind) {
      const surfaceKind = asString(merged.type || merged.surfaceKind || '', '')
        .trim()
        .toLowerCase()
      if (surfaceKind === 'injected' || surfaceKind === 'floating') {
        merged.kind = surfaceKind
      }
    }

    const legacyHintResolved = parsePositiveInt(merged.widgetInternalAgentId)
    const legacyExternal = resolveExternalWidgetAgentIdFromCandidates(
      [
        merged.publicAgentId,
        merged.chatId,
        merged.agentId,
        merged.ometricsAI?.ometricsAiId,
      ],
      legacyHintResolved,
    )
    if (legacyExternal) {
      merged.agentId = legacyExternal
      merged.chatId = legacyExternal
      if (merged.ometricsAI && typeof merged.ometricsAI === 'object') {
        merged.ometricsAI = {
          ...merged.ometricsAI,
          ometricsAiId: legacyExternal,
        }
      }
    }
    applyTemplateDerivationFromDisplayMode(merged)
    applyPreviewMobileLayout(merged)
    resolveLegacyStreamingEndpoints(merged, safeOverrides)
    const liftedDefaultCardImage = pickDefaultCardImage(merged, merged.theme, merged.agent)
    if (liftedDefaultCardImage) {
      merged.defaultCardImage = liftedDefaultCardImage
    }
    // Preview / legacy bootstrap nests AI Display Settings under `configuration`
    // (see BotManController::mergeSharedConfig). defaultConf() already sets
    // strip defaults, so we must prefer the nested values explicitly.
    const nestedCfg =
      merged.configuration &&
      typeof merged.configuration === 'object' &&
      !Array.isArray(merged.configuration)
        ? merged.configuration
        : null
    const nestedShared =
      nestedCfg?.shared &&
      typeof nestedCfg.shared === 'object' &&
      !Array.isArray(nestedCfg.shared)
        ? nestedCfg.shared
        : null
    const showStripRaw =
      nestedCfg?.showSuggestionsStrip ??
      nestedShared?.showSuggestionsStrip ??
      merged.showSuggestionsStrip
    merged.showSuggestionsStrip = showStripRaw !== false
    merged.suggestionStripSize = normalizeSuggestionStripSize(
      nestedCfg?.suggestionStripSize ??
        nestedShared?.suggestionStripSize ??
        merged.suggestionStripSize,
    )
    const maxSuggestionsRaw =
      nestedCfg?.maxSuggestions ??
      nestedShared?.maxSuggestions ??
      merged.maxSuggestions
    const maxSuggestionsN = Number(maxSuggestionsRaw)
    if (Number.isFinite(maxSuggestionsN) && maxSuggestionsN >= 1) {
      merged.maxSuggestions = Math.min(50, Math.max(1, Math.round(maxSuggestionsN)))
    }
    traceOrderStatusResolved(merged, payload, 'normalizeConfig:legacy')
    return merged
  }

  const agent = payload.agent ?? {}
  const theme = payload.theme ?? {}
  const layout = theme.layout ?? {}
  const streaming = payload.streaming ?? {}
  const publicChat = payload.public_chat ?? payload.publicChat ?? {}
  const meta = payload.meta ?? {}
  const apiBaseUrl = asString(
    overrides.apiBaseUrl || meta.apiBaseUrl || publicChat.apiBaseUrl,
  )

  const internalHintEnvelope =
    parsePositiveInt(payload.widgetInternalAgentId) ??
    parsePositiveInt(meta.widgetInternalAgentId)

  const externalAgentId =
    resolveExternalWidgetAgentIdFromCandidates(
      [
        payload.publicAgentId,
        meta.publicAgentId,
        meta.public_agent_id,
        agent.publicId,
        agent.public_id,
        payload.chatId,
        payload.agentId,
      ],
      internalHintEnvelope,
    ) ||
    resolveExternalWidgetAgentIdFromCandidates(
      [agent.id, meta.agentId],
      internalHintEnvelope,
    ) ||
    asString(agent.id || meta.agentId || '', '').trim()

  const flat = {
    ...defaultConf(),
    chatId: externalAgentId,
    agentId: externalAgentId,
    masterKey: asString(meta.masterKey || overrides.masterKey),
    sessionId: asString(meta.sessionId, generateUuid()),
    type: asString(agent.type || layout.type || 'web', 'web'),
    isWebAi: Boolean(agent.isWebAi ?? agent.is_web_ai ?? false),
    icon: asString(theme.icon || agent.icon || ''),
    widgetBubbleIcon: sanitizeWidgetBubbleIconSvg(
      payload.widget_bubble_icon ||
        theme.widgetBubbleIcon ||
        agent.widget_bubble_icon,
    ),
    widget_bubble_color: asString(
      payload.widget_bubble_color ||
        theme.colors?.bubbleForeground ||
        agent.widget_bubble_color ||
        '',
    ),
    widget_bubble_background_color: asString(
      payload.widget_bubble_background_color ||
        theme.colors?.bubbleBackground ||
        agent.widget_bubble_background_color ||
        '',
    ),
    chatServer: resolveChatServer({ streaming, publicChat, apiBaseUrl }),
    streamingEndpoint: resolveStreamingEndpoint({ streaming, apiBaseUrl }),
    fallbackEndpoint: resolveFallbackEndpoint({ streaming, apiBaseUrl }),
    streamingEnabled: streaming.enabled !== false,
    frameEndpoint: asString(publicChat.frameEndpoint || ''),
    apiBaseUrl,
    widgetTitle: pickEnvelopeWidgetTitle(theme, agent, payload),
    widgetSubtitle: pickEnvelopeWidgetSubtitle(theme, agent),
    widgetButtonColor: pickColor(
      theme,
      'buttonColor',
      pickColor(theme, 'primary', '#0D870D'),
    ),
    widgetButtonTextColor: pickColor(theme, 'buttonTextColor', '#ffffff'),
    widgetUserMessageBackgroundColor: pickColor(
      theme,
      'userMessageBackgroundColor',
      '#007aff',
    ),
    widgetUserMessageTextColor: pickColor(
      theme,
      'userMessageTextColor',
      '#ffffff',
    ),
    widgetBotMessageBackgroundColor: pickColor(
      theme,
      'botMessageBackgroundColor',
      '#e9ecef',
    ),
    widgetBotMessageTextColor: pickColor(
      theme,
      'botMessageTextColor',
      '#2b2b2b',
    ),
    widgetHeaderBackgroundColor: pickColor(theme, 'headerBackgroundColor', ''),
    widgetHeaderTextColor: pickColor(
      theme,
      'headerTextColor',
      pickColor(theme, 'text', ''),
    ),
    widgetPanelBackgroundColor: pickColor(
      theme,
      'panelBackgroundColor',
      pickColor(theme, 'background', ''),
    ),
    widgetInputBackgroundColor: pickColor(theme, 'inputBackgroundColor', ''),
    widgetInputTextColor: pickColor(theme, 'inputTextColor', ''),
    defaultCardImage: pickDefaultCardImage(payload, theme, agent),
    widget_placeholder_text: asString(payload.widget_placeholder_text || '', ''),
    composerPlaceholder: asString(
      payload.widget_placeholder_text ??
        agent?.widget_placeholder_text ??
        theme.composerPlaceholder ??
        '',
    ),
    fontFamily: asString(theme.fontFamily || ''),
    customCss: pickCustomCss(payload, theme, agent),
    displayMode: asString(
      layout.displayMode || payload.displayMode || 'overlay',
      'overlay',
    ),
    template: asString(
      layout.template ||
        deriveTemplateKeyFromDisplayMode(
          layout.displayMode || payload.displayMode,
        ) ||
        'panel',
      'panel',
    ),
    floatingPosition: asString(
      layout.floatingPosition || payload.position || 'LOWER_RIGHT',
      'LOWER_RIGHT',
    ),
    floatingMarginTop: asString(
      layout.floatingMarginTop || payload.marginTop || '0px',
      '0px',
    ),
    floatingMarginBottom: asString(
      layout.floatingMarginBottom || payload.marginBottom || '0px',
      '0px',
    ),
    floatingMarginLeft: asString(
      layout.floatingMarginLeft || payload.marginLeft || '0px',
      '0px',
    ),
    floatingMarginRight: asString(
      layout.floatingMarginRight || payload.marginRight || '0px',
      '0px',
    ),
    slideOutPanelEdge: asString(layout.slideOutPanelEdge || 'right', 'right'),
    slideOutPanelWidth: clampSlideOutPanelWidthPx(layout.slideOutPanelWidth),
    slideOutPanelMaxHeight: resolveSlideOutPanelMaxHeightFromSources(
      layout,
      payload.slideOutConfig,
    ),
    slideOutMinWidth:
      layout.slideOutMinWidth != null && Number.isFinite(Number(layout.slideOutMinWidth))
        ? Math.round(Number(layout.slideOutMinWidth))
        : undefined,
    slideOutMaxWidth:
      layout.slideOutMaxWidth != null && Number.isFinite(Number(layout.slideOutMaxWidth))
        ? Math.round(Number(layout.slideOutMaxWidth))
        : undefined,
    maxSuggestions: (() => {
      const n = Number(
        theme.maxSuggestions ??
          payload.maxSuggestions ??
          payload.configuration?.maxSuggestions,
      )
      if (!Number.isFinite(n) || n < 1) return 4
      return Math.min(50, Math.max(1, Math.round(n)))
    })(),
    showSuggestionsStrip:
      theme.showSuggestionsStrip !== false &&
      payload.showSuggestionsStrip !== false &&
      payload.configuration?.showSuggestionsStrip !== false,
    suggestionStripSize: normalizeSuggestionStripSize(
      theme.suggestionStripSize ??
        payload.suggestionStripSize ??
        payload.configuration?.suggestionStripSize,
    ),
    isMobile: false,
    mobileWrapperHeight: Number(layout.mobileWrapperHeight) || 600,
    wrapperHeight: Number(layout.wrapperHeight) || 480,
    // Default off unless explicitly enabled (matches requested behavior).
    displayMessageTime: layout.displayMessageTime === true,
    timeFormat: asString(layout.timeFormat || 'shortTime'),
    dateTimeFormat: asString(layout.dateTimeFormat || 'mediumDate'),
    activeCtas:
      payload.activeCtas ||
      payload.ctas ||
      agent.ctas ||
      agent.activeCtas ||
      [],
    activeLeadForms:
      payload.activeLeadForms ||
      payload.leadForms ||
      agent.leadForms ||
      agent.activeLeadForms ||
      [],
    ctas:
      payload.ctas ||
      payload.activeCtas ||
      agent.ctas ||
      agent.activeCtas ||
      [],
    leadForms:
      payload.leadForms ||
      payload.activeLeadForms ||
      agent.leadForms ||
      agent.activeLeadForms ||
      [],
    orderStatus: (() => {
      const picked = pickOrderStatusFromUnifiedEnvelope(
        /** @type {Record<string, unknown>} */ (payload),
      )
      return isPlainOrderStatusObject(picked) ? picked : { enabled: false }
    })(),
    siteLanguage: asString(
      payload.siteLanguage ?? agent.siteLanguage ?? '',
    ),
    menuItems: pickMenuItemsFromPayload(payload),
    menuEcommerce: pickMenuEcommerceFromPayload(payload),
    defaultMenuConversationId: (() => {
      const n = pickDefaultMenuConversationIdFromUnifiedEnvelope(
        /** @type {Record<string, unknown>} */ (payload),
      )
      return n != null && Number.isFinite(n) ? n : null
    })(),
    sendWidgetOpenedWhisper:
      payload.sendWidgetOpenedWhisper ?? agent.sendWidgetOpenedWhisper,
    widgetOpenedWhisperData: asString(
      payload.widgetOpenedWhisperData ?? agent.widgetOpenedWhisperData ?? '',
    ),
    forceWidgetOpenedWhisper:
      payload.forceWidgetOpenedWhisper ?? agent.forceWidgetOpenedWhisper,
    launchConversationId:
      payload.launchConversationId ?? agent.launchConversationId,
    triggerConfig:
      typeof payload.triggerConfig === 'object' && payload.triggerConfig !== null
        ? payload.triggerConfig
        : {},
    urlFilterTriggers:
      typeof payload.urlFilterTriggers === 'object' &&
      payload.urlFilterTriggers !== null
        ? payload.urlFilterTriggers
        : null,
    kind: asString(payload.kind || '', ''),
    widgetType: asString(payload.widgetType || theme.widgetType || '', ''),
    searchBoxType: asString(payload.searchBoxType || '', ''),
    searchTexts: normalizeSearchTexts(payload.searchTexts),
    slideOutConfig:
      typeof payload.slideOutConfig === 'object' && payload.slideOutConfig !== null
        ? payload.slideOutConfig
        : null,
    buttonText: asString(
      payload.buttonText || payload.configuration?.buttonText || '',
      '',
    ),
    buttonColor: asString(
      payload.buttonColor || payload.configuration?.buttonColor || '',
      '',
    ),
    buttonTextColor: asString(
      payload.buttonTextColor || payload.configuration?.buttonTextColor || '',
      '',
    ),
    buttonBorderColor: asString(
      payload.buttonBorderColor || payload.configuration?.buttonBorderColor || '',
      '',
    ),
    buttonBorderRadius: asString(
      payload.buttonBorderRadius || payload.configuration?.buttonBorderRadius || '',
      '',
    ),
    buttonIcon: asString(
      payload.buttonIcon || payload.configuration?.buttonIcon || '',
      '',
    ),
    buttonIconPosition: asString(
      payload.buttonIconPosition || payload.configuration?.buttonIconPosition || '',
      '',
    ),
    buttonIconAnimation: asString(
      payload.buttonIconAnimation || payload.configuration?.buttonIconAnimation || '',
      '',
    ),
    injectionSelector: asString(payload.injectionSelector || '', ''),
    injectionType: asString(payload.injectionType || '', ''),
    displayType: asString(payload.displayType || '', ''),
    ...pickEcommerceTriggerConfig(
      payload,
      typeof payload.configuration === 'object' &&
        payload.configuration !== null &&
        !Array.isArray(payload.configuration)
        ? payload.configuration
        : null,
    ),
    layout: asString(payload.layout || agent.layout || layout.layout || ''),
    ometricsAI: {
      ometricsAiId: externalAgentId,
      aiType: asString(agent.type || 'web', 'web'),
      displayMode: asString(layout.displayMode || 'overlay'),
      template: asString(layout.template || 'panel'),
      inlineSuggestions: layout.inlineSuggestions,
      customCss: pickCustomCss(payload, theme, agent),
      floatingPosition: asString(
        layout.floatingPosition || payload.position || 'LOWER_RIGHT',
        'LOWER_RIGHT',
      ),
      slideOutPanelEdge: asString(layout.slideOutPanelEdge || 'right', 'right'),
      slideOutPanelWidth: clampSlideOutPanelWidthPx(layout.slideOutPanelWidth),
      slideOutPanelMaxHeight: resolveSlideOutPanelMaxHeightFromSources(
        layout,
        payload.slideOutConfig,
      ),
      maxSuggestions: (() => {
        const n = Number(
          theme.maxSuggestions ??
            payload.maxSuggestions ??
            payload.configuration?.maxSuggestions,
        )
        if (!Number.isFinite(n) || n < 1) return 4
        return Math.min(50, Math.max(1, Math.round(n)))
      })(),
      showSuggestionsStrip:
        theme.showSuggestionsStrip !== false &&
        payload.showSuggestionsStrip !== false &&
        payload.configuration?.showSuggestionsStrip !== false,
      suggestionStripSize: normalizeSuggestionStripSize(
        theme.suggestionStripSize ??
          payload.suggestionStripSize ??
          payload.configuration?.suggestionStripSize,
      ),
    },
    raw: payload,
  }
  applySnakeCaseWidgetColors(flat)

  applyTemplateDerivationFromDisplayMode(flat)
  applySlideOutConfigToConf(flat)
  if (flat.ometricsAI && typeof flat.ometricsAI === 'object') {
    flat.ometricsAI = {
      ...flat.ometricsAI,
      slideOutPanelEdge: flat.slideOutPanelEdge,
      slideOutPanelWidth: flat.slideOutPanelWidth,
      slideOutPanelMaxHeight: flat.slideOutPanelMaxHeight,
    }
  }

  const out = {
    ...flat,
    ...stripConflictingInternalAgentIdOverrides(overrides, internalHintEnvelope),
  }
  traceOrderStatusResolved(out, payload, 'normalizeConfig:envelope')
  return out
}

function resolveChatServer({ streaming, publicChat, apiBaseUrl }) {
  if (publicChat?.chatServer) return asString(publicChat.chatServer)
  if (streaming?.fallback_endpoint) {
    return joinUrl(apiBaseUrl, streaming.fallback_endpoint)
  }
  return joinUrl(apiBaseUrl, '/api/v1/widget/chat/respond')
}

function resolveStreamingEndpoint({ streaming, apiBaseUrl }) {
  if (streaming?.endpoint) return joinUrl(apiBaseUrl, streaming.endpoint)
  return joinUrl(apiBaseUrl, '/api/v1/widget/chat/stream')
}

function resolveFallbackEndpoint({ streaming, apiBaseUrl }) {
  if (streaming?.fallback_endpoint)
    return joinUrl(apiBaseUrl, streaming.fallback_endpoint)
  return joinUrl(apiBaseUrl, '/api/v1/widget/chat/respond')
}

function joinUrl(base, path) {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  if (!base) return path
  if (base.endsWith('/') && path.startsWith('/'))
    return base + path.substring(1)
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`
  return base + path
}

function defaultConf() {
  return {
    chatId: '',
    agentId: '',
    masterKey: '',
    sessionId: generateUuid(),
    type: 'web',
    isWebAi: false,
    icon: '',
    widgetBubbleIcon: '',
    widget_bubble_color: '',
    widget_bubble_background_color: '',
    sendWidgetOpenedWhisper: undefined,
    widgetOpenedWhisperData: '',
    forceWidgetOpenedWhisper: undefined,
    launchConversationId: undefined,
    triggerConfig: {},
    urlFilterTriggers: null,
    layout: '',
    chatServer: '',
    streamingEndpoint: '',
    fallbackEndpoint: '',
    streamingEnabled: true,
    frameEndpoint: '',
    apiBaseUrl: '',
    widgetTitle: 'Chat',
    widgetSubtitle: '',
    widgetButtonColor: '#0D870D',
    widgetButtonTextColor: '#ffffff',
    widgetUserMessageBackgroundColor: '#007aff',
    widgetUserMessageTextColor: '#ffffff',
    widgetBotMessageBackgroundColor: '#e9ecef',
    widgetBotMessageTextColor: '#2b2b2b',
    widgetHeaderBackgroundColor: '',
    widgetHeaderTextColor: '',
    widgetPanelBackgroundColor: '',
    widgetInputBackgroundColor: '',
    widgetInputTextColor: '',
    composerPlaceholder: '',
    widget_placeholder_text: '',
    fontFamily: '',
    customCss: '',
    displayMode: 'overlay',
    template: 'panel',
    floatingPosition: 'LOWER_RIGHT',
    floatingMarginTop: '0px',
    floatingMarginBottom: '0px',
    floatingMarginLeft: '0px',
    floatingMarginRight: '0px',
    slideOutPanelEdge: 'right',
    slideOutPanelWidth: 400,
    slideOutPanelMaxHeight: SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX,
    slideOutMinWidth: undefined,
    slideOutMaxWidth: undefined,
    maxSuggestions: 4,
    showSuggestionsStrip: true,
    suggestionStripSize: 'medium',
    isMobile: false,
    mobileWrapperHeight: 600,
    wrapperHeight: 480,
    displayMessageTime: true,
    timeFormat: 'shortTime',
    dateTimeFormat: 'mediumDate',
    activeCtas: [],
    activeLeadForms: [],
    ctas: [],
    leadForms: [],
    orderStatus: { enabled: false, showSuggestionStrip: true },
    defaultMenuConversationId: null,
    ometricsAI: {
      ometricsAiId: '',
      aiType: 'web',
      displayMode: 'overlay',
      template: 'panel',
      floatingPosition: 'LOWER_RIGHT',
      slideOutPanelEdge: 'right',
      slideOutPanelWidth: 400,
      slideOutPanelMaxHeight: SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX,
      maxSuggestions: 4,
      showSuggestionsStrip: true,
      suggestionStripSize: 'medium',
    },
    kind: '',
  }
}
